import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ChakraProvider, Box, Container, Spinner } from '@chakra-ui/react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import theme from './utils/theme';

// Layout Components
import Navbar from './components/layout/Navbar';
import ProtectedRoute from './components/ProtectedRoute';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';

// Client Pages 
import Dashboard from './pages/client/Dashboard';
import TicketsList from './pages/client/TicketsList';
import TicketDetails from './pages/client/TicketDetails';
import NewTicket from './pages/client/NewTicket';
import EditTicket from './pages/client/EditTicket';

// Landing Page
const Home = () => {
  const { isAuthenticated, loading } = useAuth();
  
  // Wait for authentication check to complete before redirecting
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="70vh">
        <ChakraProvider>
          <Spinner size="xl" color="blue.500" />
        </ChakraProvider>
      </Box>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Navigate to="/login" replace />;
};

// Layout wrapper with Navbar
const Layout = ({ children }) => (
  <Box minH="100vh" bg="gray.50">
    <Navbar />
    <Container maxW="container.xl" py={6}>
      {children}
    </Container>
  </Box>
);

const App = () => {
  return (
    <ChakraProvider theme={theme}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/login" element={<Layout><Login /></Layout>} />
            <Route path="/register" element={<Layout><Register /></Layout>} />
            
            {/* Protected routes - Client */}
            <Route path="/dashboard" element={
              <Layout>
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              </Layout>
            } />
            
            <Route path="/tickets" element={
              <Layout>
                <ProtectedRoute>
                  <TicketsList />
                </ProtectedRoute>
              </Layout>
            } />
            
            <Route path="/tickets/new" element={
              <Layout>
                <ProtectedRoute>
                  <NewTicket />
                </ProtectedRoute>
              </Layout>
            } />
            
            <Route path="/tickets/:id" element={
              <Layout>
                <ProtectedRoute>
                  <TicketDetails />
                </ProtectedRoute>
              </Layout>
            } />
            
            <Route path="/tickets/:id/edit" element={
              <Layout>
                <ProtectedRoute>
                  <EditTicket />
                </ProtectedRoute>
              </Layout>
            } />
            
            {/* Fallback for unknown routes */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ChakraProvider>
  );
};

export default App;
